import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Modal, Select, TextInput } from '@ministry/ui/internal';

import { onboardTenant, updateTenant, type OnboardTenantResult, type Tenant, type TenantStatus } from '../../lib/admin-platform.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { genericFieldErrorMessage } from '../../lib/field-errors.js';
import { useT } from '../../i18n/strings.js';

export interface OnboardTenantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (result: OnboardTenantResult) => void;
}

/**
 * SCREEN_SPECS.md I21 — new-ministry onboarding. The prefix's immutability is stated
 * BEFORE confirmation, per spec's own required copy: "This prefix cannot be changed once
 * the first file is registered under it."
 */
export function OnboardTenantModal({ open, onOpenChange, onSuccess }: OnboardTenantModalProps): React.JSX.Element {
  const t = useT();
  const [codePrefix, setCodePrefix] = React.useState('');
  const [nameNe, setNameNe] = React.useState('');
  const [nameEn, setNameEn] = React.useState('');
  const [rootOrgUnitCode, setRootOrgUnitCode] = React.useState('');
  const [secretaryUsername, setSecretaryUsername] = React.useState('');
  const [secretaryFullName, setSecretaryFullName] = React.useState('');
  const [secretaryPassword, setSecretaryPassword] = React.useState('');
  const [touched, setTouched] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!open) {
      setCodePrefix('');
      setNameNe('');
      setNameEn('');
      setRootOrgUnitCode('');
      setSecretaryUsername('');
      setSecretaryFullName('');
      setSecretaryPassword('');
      setTouched({});
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      onboardTenant({
        codePrefix: codePrefix.trim().toUpperCase(),
        nameNe: nameNe.trim(),
        nameEn: nameEn.trim(),
        rootOrgUnitCode: rootOrgUnitCode.trim(),
        secretaryUsername: secretaryUsername.trim(),
        secretaryFullName: secretaryFullName.trim(),
        secretaryPassword,
      }),
    onSuccess: (result) => {
      onOpenChange(false);
      onSuccess(result);
    },
  });

  const prefixError =
    touched.codePrefix && !/^[A-Za-z]{2,6}$/.test(codePrefix.trim()) ? t('admin.tenant.create.prefix.error') : undefined;
  const requiredError = (value: string, key: string) => (touched[key] && value.trim().length === 0 ? t('admin.tenant.create.required') : undefined);

  const fieldErrorFor = (field: string): string | undefined => {
    if (!(mutation.error instanceof ApiError)) return undefined;
    const match = mutation.error.fieldErrors.find((fe) => fe.field === field);
    if (!match) return undefined;
    return genericFieldErrorMessage(t, match.code, match.params);
  };

  const isValid =
    /^[A-Za-z]{2,6}$/.test(codePrefix.trim()) &&
    nameNe.trim() !== '' &&
    nameEn.trim() !== '' &&
    rootOrgUnitCode.trim() !== '' &&
    secretaryUsername.trim() !== '' &&
    secretaryFullName.trim() !== '' &&
    secretaryPassword !== '';

  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === 'CONFLICT_DUPLICATE') {
        return <AlertBanner variant="critical" title={t('admin.tenant.create.error.duplicatePrefix')} />;
      }
      const RENDERED = new Set(['codePrefix', 'nameNe', 'nameEn', 'rootOrgUnitCode', 'secretaryUsername', 'secretaryFullName', 'secretaryPassword']);
      const unrenderable = mutation.error.fieldErrors.filter((fe) => !RENDERED.has(fe.field));
      if (unrenderable.length > 0) {
        return <AlertBanner variant="critical" title={unrenderable.map((fe) => genericFieldErrorMessage(t, fe.code, fe.params)).join(' ')} />;
      }
      if (mutation.error.fieldErrors.length > 0) return <AlertBanner variant="critical" title={t('admin.error.checkFields')} />;
      return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
    }
    return null;
  })();

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.tenant.create.title')}
      description={t('admin.tenant.create.description')}
      confirmLabel={mutation.isPending ? t('admin.tenant.create.submitting') : t('admin.tenant.create.submit')}
      cancelLabel={t('admin.common.cancel')}
      loading={mutation.isPending}
      size="large"
      onConfirm={() => {
        setTouched({
          codePrefix: true,
          nameNe: true,
          nameEn: true,
          rootOrgUnitCode: true,
          secretaryUsername: true,
          secretaryFullName: true,
          secretaryPassword: true,
        });
        if (!isValid) return;
        mutation.mutate();
      }}
    >
      <div className="flex flex-col gap-4">
        {banner}
        <TextInput
          id="admin-tenant-create-prefix"
          label={t('admin.tenant.create.prefix.label')}
          required
          value={codePrefix}
          onChange={(event) => setCodePrefix(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, codePrefix: true }))}
          errorText={prefixError ?? fieldErrorFor('codePrefix')}
          helperText={t('admin.tenant.create.prefix.hint', { prefix: codePrefix.trim().toUpperCase() || 'XX' })}
        />
        <TextInput
          id="admin-tenant-create-namene"
          label={t('admin.tenant.create.nameNe.label')}
          required
          value={nameNe}
          onChange={(event) => setNameNe(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, nameNe: true }))}
          errorText={requiredError(nameNe, 'nameNe') ?? fieldErrorFor('nameNe')}
        />
        <TextInput
          id="admin-tenant-create-nameen"
          label={t('admin.tenant.create.nameEn.label')}
          required
          value={nameEn}
          onChange={(event) => setNameEn(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, nameEn: true }))}
          errorText={requiredError(nameEn, 'nameEn') ?? fieldErrorFor('nameEn')}
        />
        <TextInput
          id="admin-tenant-create-rootcode"
          label={t('admin.tenant.create.rootOrgUnitCode.label')}
          required
          value={rootOrgUnitCode}
          onChange={(event) => setRootOrgUnitCode(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, rootOrgUnitCode: true }))}
          errorText={requiredError(rootOrgUnitCode, 'rootOrgUnitCode') ?? fieldErrorFor('rootOrgUnitCode')}
          helperText={t('admin.tenant.create.rootOrgUnitCode.hint')}
        />
        <hr className="border-border-decorative" />
        <p className="text-sm font-semibold text-text-heading">{t('admin.tenant.create.secretary.heading')}</p>
        <TextInput
          id="admin-tenant-create-secretary-username"
          label={t('admin.tenant.create.secretary.username.label')}
          required
          value={secretaryUsername}
          onChange={(event) => setSecretaryUsername(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, secretaryUsername: true }))}
          errorText={requiredError(secretaryUsername, 'secretaryUsername') ?? fieldErrorFor('secretaryUsername')}
        />
        <TextInput
          id="admin-tenant-create-secretary-fullname"
          label={t('admin.tenant.create.secretary.fullName.label')}
          required
          value={secretaryFullName}
          onChange={(event) => setSecretaryFullName(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, secretaryFullName: true }))}
          errorText={requiredError(secretaryFullName, 'secretaryFullName') ?? fieldErrorFor('secretaryFullName')}
        />
        <TextInput
          id="admin-tenant-create-secretary-password"
          label={t('admin.tenant.create.secretary.password.label')}
          required
          type="password"
          value={secretaryPassword}
          onChange={(event) => setSecretaryPassword(event.target.value)}
          onBlur={() => setTouched((prev) => ({ ...prev, secretaryPassword: true }))}
          errorText={requiredError(secretaryPassword, 'secretaryPassword') ?? fieldErrorFor('secretaryPassword')}
        />
      </div>
    </Modal>
  );
}

export interface EditTenantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant: Tenant | null;
  onSuccess: (tenant: Tenant) => void;
}

export function EditTenantModal({ open, onOpenChange, tenant, onSuccess }: EditTenantModalProps): React.JSX.Element | null {
  const t = useT();
  const [status, setStatus] = React.useState<TenantStatus>('active');
  const [nameNe, setNameNe] = React.useState('');
  const [nameEn, setNameEn] = React.useState('');

  React.useEffect(() => {
    if (open && tenant) {
      setStatus(tenant.status as TenantStatus);
      setNameNe(tenant.nameNe);
      setNameEn(tenant.nameEn);
    }
  }, [open, tenant]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!tenant) throw new Error('No tenant selected.');
      return updateTenant(tenant.id, { status, nameNe: nameNe.trim(), nameEn: nameEn.trim() });
    },
    onSuccess: (updated) => {
      onOpenChange(false);
      onSuccess(updated);
    },
  });

  if (!tenant) return null;

  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
  })();

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.tenant.edit.title', { name: tenant.nameEn })}
      confirmLabel={mutation.isPending ? t('admin.common.saving') : t('admin.common.save')}
      cancelLabel={t('admin.common.cancel')}
      loading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
    >
      <div className="flex flex-col gap-4">
        {banner}
        {status === 'suspended' ? <AlertBanner variant="warning" title={t('admin.tenant.edit.suspendWarning')} /> : null}
        <Select
          id="admin-tenant-edit-status"
          label={t('admin.tenant.edit.status.label')}
          value={status}
          onValueChange={(value) => setStatus(value as TenantStatus)}
          options={[
            { value: 'active', label: t('admin.tenant.status.active') },
            { value: 'suspended', label: t('admin.tenant.status.suspended') },
          ]}
        />
        <TextInput
          id="admin-tenant-edit-namene"
          label={t('admin.tenant.create.nameNe.label')}
          value={nameNe}
          onChange={(event) => setNameNe(event.target.value)}
        />
        <TextInput
          id="admin-tenant-edit-nameen"
          label={t('admin.tenant.create.nameEn.label')}
          value={nameEn}
          onChange={(event) => setNameEn(event.target.value)}
        />
      </div>
    </Modal>
  );
}
